import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileCheck2, Upload, Clock, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const STATUS_BADGE = {
  nao_enviado: { l: "Não enviado", c: "bg-[#F4F7FB] text-aj-text-muted" },
  enviado: { l: "Em análise", c: "bg-[#DBEAFE] text-[#142766]" },
  aprovado: { l: "Aprovado", c: "bg-[#DCFCE7] text-[#166534]" },
  rejeitado: { l: "Rejeitado", c: "bg-red-100 text-[#B91C1C]" },
};

const SUBTIPOS = [
  { v: "cin", l: "CIN" },
  { v: "rg", l: "RG" },
  { v: "cnh", l: "CNH" },
];

export const DocumentosMontador = () => {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(null);
  const [subtipo, setSubtipo] = useState("rg");
  const inputRefs = useRef({});

  const load = useCallback(() => {
    api.get("/montador/meus-documentos").then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);

  const enviar = async (tipo, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo acima de 10MB"); return; }
    setSending(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      if (tipo === "doc_identidade") fd.append("subtipo", subtipo);
      await api.post("/montador/documentos", fd);
      toast.success("Documento enviado para análise");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(null); }
  };

  if (!data) return <div className="text-sm text-aj-text-muted py-4 text-center">Carregando documentos…</div>;

  const pendentes = data.checklist.filter(c => c.obrigatorio && c.status !== "aprovado" && c.status !== "enviado").length;

  return (
    <div className="text-left space-y-3" data-testid="documentos-montador">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-aj-orange" />
        <h2 className="font-display font-bold text-base text-aj-blue-deep">Documentos do cadastro</h2>
      </div>
      <p className="text-xs text-aj-text-muted">
        Envie os documentos abaixo para a análise da equipe MontaJá (prazo esperado de até <b>72h</b> após o envio completo).
        {pendentes > 0 && <span className="block mt-1 font-semibold text-aj-orange">{pendentes} documento(s) obrigatório(s) pendente(s).</span>}
      </p>

      {data.checklist.map(item => {
        const badge = STATUS_BADGE[item.status] || STATUS_BADGE.nao_enviado;
        return (
          <div key={item.tipo} className="border border-border rounded-lg p-3 bg-white" data-testid={`doc-item-${item.tipo}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-aj-text flex items-center gap-2 flex-wrap">
                  {item.status === "aprovado" ? <CheckCircle2 size={14} className="text-[#166534]" />
                    : item.status === "rejeitado" ? <XCircle size={14} className="text-[#B91C1C]" />
                    : item.status === "enviado" ? <Clock size={14} className="text-[#142766]" />
                    : <FileCheck2 size={14} className="text-aj-text-muted" />}
                  {item.label}
                  {item.obrigatorio && <span className="text-[10px] font-bold uppercase text-aj-orange">obrigatório</span>}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${badge.c}`} data-testid={`doc-status-${item.tipo}`}>{badge.l}</span>
                  {item.subtipo && <span className="text-[10px] uppercase font-bold text-aj-text-muted">{item.subtipo}</span>}
                  {item.motivo_rejeicao && <span className="text-xs text-[#B91C1C]">Motivo: {item.motivo_rejeicao}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {item.tipo === "doc_identidade" && item.status !== "aprovado" && (
                  <select value={subtipo} onChange={e => setSubtipo(e.target.value)}
                    className="border border-border rounded-md text-xs px-2 py-1.5 bg-white"
                    data-testid="doc-subtipo-select">
                    {SUBTIPOS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                )}
                {item.status !== "aprovado" && (
                  <>
                    <input type="file" accept="image/*,.pdf" className="hidden"
                      ref={el => (inputRefs.current[item.tipo] = el)}
                      onChange={e => enviar(item.tipo, e.target.files?.[0])} />
                    <Button size="sm" variant="outline" disabled={sending === item.tipo}
                      onClick={() => inputRefs.current[item.tipo]?.click()}
                      data-testid={`doc-enviar-${item.tipo}`}>
                      <Upload size={13} /> {sending === item.tipo ? "Enviando…" : item.status === "nao_enviado" ? "Enviar" : "Reenviar"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <p className="text-[11px] text-aj-text-muted">
        Documento com foto: basta <b>um</b> entre CIN, RG ou CNH. Sua selfie será moderada pela equipe e só aparece ao cliente após a confirmação de um serviço.
      </p>
    </div>
  );
};

export default DocumentosMontador;
